/**
 * The viewer's two panes and the handle between them.
 *
 * The handle is one of the few genuinely *physical* things in this app, so
 * it uses physical left/right rather than logical properties -- and the
 * panes do not mirror in Arabic. The page rail stays on the same side in
 * both UI languages because it is spatially tied to a document image whose
 * own layout does not flip; mirroring the chrome around a fixed image reads
 * as a bug (§11.1).
 *
 * The split has two axes, chosen by the caller from the viewport:
 *
 *   x  side by side. The desktop reading, and the one the viewer is for.
 *   y  stacked. A filing page is portrait, so on a portrait tablet a
 *      vertical split hands the page half the width while a horizontal one
 *      hands it all of the width and half the height -- and a tall page
 *      scrolls the way a page is supposed to.
 *
 * A phone gets neither: see the mode switch in the viewer, which shows one
 * pane at a time. Below about 400px there is no split worth having.
 *
 * The ratio is persisted per axis. One stored number shared between a
 * vertical and a horizontal split is a value that fights itself -- 0.3 is a
 * narrow left pane and a squashed top one, and the reader meant only one of
 * them.
 */
import {
  useCallback, useEffect, useRef, useState, type ReactNode,
} from 'react'
import { cn } from '~/lib/cn.ts'

export type SplitAxis = 'x' | 'y'

const KEY = 'explorer.split'
const keyFor = (axis: SplitAxis) => (axis === 'x' ? KEY : `${KEY}.y`)

export function SplitPane({
  left, right, axis = 'x', min = 0.25, max = 0.75, defaultRatio = 0.52,
  className, label = 'Resize panes',
}: {
  left: ReactNode
  right: ReactNode
  /** `x` side by side, `y` stacked. */
  axis?: SplitAxis
  min?: number
  max?: number
  defaultRatio?: number
  className?: string
  label?: string
}) {
  const [ratio, setRatio] = useState(() => read(axis, defaultRatio, min, max))
  const [dragging, setDragging] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Switching axis (rotating a tablet) loads that axis's own remembered
  // ratio rather than carrying the other one across.
  useEffect(() => {
    setRatio(read(axis, defaultRatio, min, max))
  }, [axis, defaultRatio, min, max])

  useEffect(() => {
    try { localStorage.setItem(keyFor(axis), String(ratio)) } catch {}
  }, [axis, ratio])

  const apply = useCallback(
    (clientX: number, clientY: number) => {
      const el = ref.current
      if (!el) return
      const box = el.getBoundingClientRect()
      const span = axis === 'x' ? box.width : box.height
      if (span === 0) return
      const at = axis === 'x' ? clientX - box.left : clientY - box.top
      setRatio(Math.min(max, Math.max(min, at / span)))
    },
    [axis, min, max],
  )

  useEffect(() => {
    if (!dragging) return
    const onMove = (e: PointerEvent) => { e.preventDefault(); apply(e.clientX, e.clientY) }
    const stop = () => setDragging(false)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', stop)
    window.addEventListener('pointercancel', stop)
    // While dragging, the cursor must not flicker to a text caret over the
    // panes and a text selection must not start.
    document.body.style.cursor = axis === 'x' ? 'col-resize' : 'row-resize'
    document.body.style.userSelect = 'none'
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', stop)
      window.removeEventListener('pointercancel', stop)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [dragging, apply, axis])

  const nudge = (delta: number) =>
    setRatio((r) => Math.min(max, Math.max(min, r + delta)))

  const horizontal = axis === 'x'

  return (
    <div
      ref={ref}
      className={cn('flex min-h-0 min-w-0', horizontal ? 'flex-row' : 'flex-col', className)}
    >
      {/*
        Grow ratios, not a percentage flex-basis. A percentage basis has to
        resolve against a *definite* parent size, and on the stacked axis
        that is a parent height -- which is exactly the thing a flex column
        does not always have. Two panes growing 52 : 48 from a zero basis
        give the identical result and need nothing definite at all.
      */}
      <div
        className={horizontal ? 'min-w-0' : 'min-h-0'}
        style={{ flex: `${ratio} 1 0%` }}
      >
        {left}
      </div>
      <div
        role="separator"
        aria-orientation={horizontal ? 'vertical' : 'horizontal'}
        aria-label={label}
        aria-valuenow={Math.round(ratio * 100)}
        aria-valuemin={Math.round(min * 100)}
        aria-valuemax={Math.round(max * 100)}
        tabIndex={0}
        onPointerDown={(e) => { e.preventDefault(); setDragging(true) }}
        onDoubleClick={() => setRatio(defaultRatio)}
        onKeyDown={(e) => {
          const back = horizontal ? 'ArrowLeft' : 'ArrowUp'
          const fwd = horizontal ? 'ArrowRight' : 'ArrowDown'
          if (e.key === back) { e.preventDefault(); nudge(-0.02) }
          else if (e.key === fwd) { e.preventDefault(); nudge(0.02) }
          else if (e.key === 'Home') { e.preventDefault(); setRatio(defaultRatio) }
        }}
        className={cn(
          'group relative shrink-0 bg-[var(--border)] touch-none',
          horizontal ? 'w-px cursor-col-resize' : 'h-px cursor-row-resize',
          // The painted line stays 1px. The *grabbable* band around it is
          // 3px with a mouse and 11px with a finger -- a hairline is a
          // precision target, and a finger has none.
          horizontal
            ? 'before:absolute before:inset-y-0 before:-left-[max(3px,var(--tap))] before:-right-[max(3px,var(--tap))] before:content-[""]'
            : 'before:absolute before:inset-x-0 before:-top-[max(3px,var(--tap))] before:-bottom-[max(3px,var(--tap))] before:content-[""]',
          dragging ? 'bg-[var(--accent)]' : 'hover:bg-[var(--border-strong)]',
        )}
      />
      <div
        className={horizontal ? 'min-w-0' : 'min-h-0'}
        style={{ flex: `${1 - ratio} 1 0%` }}
      >
        {right}
      </div>
    </div>
  )
}

function read(axis: SplitAxis, fallback: number, min: number, max: number): number {
  try {
    const v = Number(localStorage.getItem(keyFor(axis)))
    if (Number.isFinite(v) && v >= min && v <= max) return v
  } catch {}
  return fallback
}
